'use client';

import * as React from 'react';
import { motion, useInView } from 'motion/react';

const EXCERPTS = [
  {
    role: 'Security director',
    source: 'Comment · 2d',
    before: 'How do we ',
    phrase: 'audit AI decisions',
    recommendation: 'audit decisions',
    after: '?',
    position: 'left-0 right-3 top-0 md:-rotate-[1.2deg]'
  },
  {
    role: 'IT leader',
    source: 'Post · 4d',
    before: 'We need ',
    phrase: 'approvals before rollout',
    recommendation: 'approve AI use',
    after: '.',
    position: 'left-3 right-0 top-[84px] md:top-[98px] md:rotate-[0.8deg]'
  },
  {
    role: 'Risk lead',
    source: 'Comment · 6d',
    before: 'Who ',
    phrase: 'owns the risk',
    recommendation: 'assign responsibility',
    after: '?',
    position: 'left-1 right-2 top-[168px] md:top-[196px] md:-rotate-[0.5deg]'
  }
] as const;

function MatchedPhrase({
  children,
  index,
  ready,
  reduced,
  emphasized
}: {
  children: React.ReactNode;
  index: number;
  ready: boolean;
  reduced: boolean;
  emphasized: boolean;
}): React.JSX.Element {
  return (
    <motion.mark
      className="rounded-[2px] bg-transparent px-0.5 text-inherit box-decoration-clone"
      style={{
        backgroundImage: 'linear-gradient(var(--mint), var(--mint))',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'left center',
        textDecorationLine: emphasized ? 'underline' : 'none',
        textDecorationColor: 'var(--accent-deep)',
        textUnderlineOffset: '4px'
      }}
      initial={reduced ? false : { backgroundSize: '0% 100%' }}
      animate={{ backgroundSize: ready ? '100% 100%' : '0% 100%' }}
      transition={{
        duration: reduced ? 0 : 0.5,
        delay: reduced || !ready ? 0 : 0.8 + index * 0.4
      }}
    >
      {children}
    </motion.mark>
  );
}

export function AudienceResearchCanvas({
  reduced
}: {
  reduced: boolean;
}): React.JSX.Element {
  const stageRef = React.useRef<HTMLDivElement>(null);
  const compositionRef = React.useRef<HTMLDivElement>(null);
  const sourceRefs = React.useRef<Array<HTMLLIElement | null>>([]);
  const targetRefs = React.useRef<Array<HTMLSpanElement | null>>([]);
  const visible = useInView(stageRef, { once: true, amount: 0.25 });
  const ready = reduced || visible;
  const [hovered, setHovered] = React.useState<number | null>(null);
  const [focused, setFocused] = React.useState<number | null>(null);
  const active = focused ?? hovered;
  const briefId = React.useId();
  const [connections, setConnections] = React.useState({
    width: 1,
    height: 1,
    paths: [] as string[]
  });

  React.useLayoutEffect(() => {
    const composition = compositionRef.current;
    if (!composition) return;

    // Layout coordinates stay stable while the outer carousel tilts and enters.
    const position = (element: HTMLElement): { x: number; y: number } => {
      let x = 0;
      let y = 0;
      let current: HTMLElement | null = element;
      while (current && current !== composition) {
        x += current.offsetLeft;
        y += current.offsetTop;
        current = current.offsetParent as HTMLElement | null;
      }
      return { x, y };
    };

    const measure = (): void => {
      const paths = sourceRefs.current.map((source, index) => {
        const finding = targetRefs.current[index];
        if (!source || !finding) return '';
        const target = position(finding);
        const endX = target.x + finding.offsetWidth / 2;
        const endY = target.y + finding.offsetHeight / 2;
        const origin = position(source);
        const x = origin.x + source.offsetWidth;
        const y = origin.y + source.offsetHeight / 2;
        const curve = Math.max((endX - x) * 0.48, 12);
        return `M ${x} ${y} C ${x + curve} ${y}, ${endX - curve} ${endY}, ${endX} ${endY}`;
      });
      setConnections({
        width: composition.clientWidth,
        height: composition.clientHeight,
        paths
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(composition);
    // Font loading or wrapped metadata can move anchors without resizing a row.
    Array.from(composition.children).forEach((column) => {
      if (column instanceof HTMLDivElement) observer.observe(column);
    });
    targetRefs.current.forEach((target) => {
      if (target) observer.observe(target);
      if (target?.parentElement) observer.observe(target.parentElement);
    });
    sourceRefs.current.forEach((source) => {
      if (source) observer.observe(source);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={stageRef}
      data-audience-preview
      className="flex h-[584px] min-w-0 flex-col overflow-hidden min-[375px]:h-[560px] md:h-[456px]"
    >
      <div
        ref={compositionRef}
        className="relative grid min-h-0 flex-1 grid-cols-1 grid-rows-[318px_minmax(0,1fr)] gap-4 px-4 py-6 min-[375px]:grid-rows-[302px_minmax(0,1fr)] sm:px-7 md:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] md:grid-rows-1 md:items-center md:gap-8 md:px-8 md:py-8 lg:gap-12 lg:px-10"
      >
        <svg
          aria-hidden
          viewBox={`0 0 ${connections.width} ${connections.height}`}
          preserveAspectRatio="none"
          className="pointer-events-none absolute inset-0 z-10 hidden size-full overflow-visible md:block"
        >
          {connections.paths.map((path, index) => (
            <motion.path
              key={EXCERPTS[index]!.role}
              d={path}
              fill="none"
              stroke="var(--accent-deep)"
              strokeWidth={active === index ? 1.6 : 1}
              initial={reduced ? false : { pathLength: 0, opacity: 0 }}
              animate={{
                pathLength: ready ? 1 : 0,
                opacity: active === null ? 0.3 : active === index ? 0.85 : 0.1
              }}
              transition={{
                pathLength: {
                  duration: reduced ? 0 : 0.7,
                  delay: reduced || !ready ? 0 : 0.8 + index * 0.4
                },
                opacity: { duration: reduced ? 0 : 0.2 }
              }}
            />
          ))}
        </svg>

        <div className="relative w-full max-w-[380px] justify-self-center">
          <p className="text-[11px] font-medium text-[color:var(--ink-2)]">
            Audience conversations
          </p>
          <p className="mt-1 text-[9px] leading-relaxed text-[color:var(--ink-4)]">
            Security leaders · Illustrative LinkedIn excerpts
          </p>
          <ol
            aria-label="Audience conversations"
            className="relative m-0 mt-3 h-[258px] w-full list-none p-0 md:mt-4 md:h-[298px]"
          >
            {EXCERPTS.map((excerpt, index) => (
              <li
                key={excerpt.role}
                ref={(node) => {
                  sourceRefs.current[index] = node;
                }}
                className={`absolute h-[94px] md:h-[102px] ${excerpt.position}`}
                style={{ zIndex: active === index ? 4 : index + 1 }}
              >
                <motion.figure
                  tabIndex={0}
                  aria-describedby={`${briefId}-recommendation-${index}`}
                  onPointerEnter={(event) => {
                    if (event.pointerType === 'mouse') setHovered(index);
                  }}
                  onPointerLeave={() => setHovered(null)}
                  onFocus={() => setFocused(index)}
                  onBlur={() => setFocused(null)}
                  initial={reduced ? false : { opacity: 0, y: 18 }}
                  animate={{ opacity: ready ? 1 : 0, y: ready ? 0 : 18 }}
                  whileHover={reduced ? undefined : { y: -3 }}
                  transition={{
                    opacity: {
                      duration: reduced ? 0 : 0.5,
                      delay: reduced || !ready ? 0 : index * 0.4
                    },
                    y: reduced
                      ? { duration: 0 }
                      : {
                          type: 'spring',
                          stiffness: 220,
                          damping: 26,
                          delay: ready ? index * 0.4 : 0
                        }
                  }}
                  className="relative m-0 flex h-full flex-col justify-center rounded-[14px] border border-[color:var(--hairline)] bg-[color:var(--paper)] px-3 py-3 shadow-[0_8px_24px_rgba(15,45,32,0.06)] outline-none transition-[border-color,box-shadow] duration-200 hover:border-[color:var(--accent-deep)]/40 hover:shadow-[0_12px_30px_rgba(15,45,32,0.09)] focus-visible:ring-2 focus-visible:ring-[color:var(--accent-deep)] focus-visible:ring-offset-2 md:px-4"
                >
                  <figcaption className="mb-2 flex items-center justify-between gap-2 text-[10px] text-[color:var(--ink-4)]">
                    <span className="font-medium text-[color:var(--ink-3)]">
                      {excerpt.role}
                    </span>
                    <span className="shrink-0">{excerpt.source}</span>
                  </figcaption>
                  <blockquote className="m-0 text-[15px] font-medium leading-[1.35] tracking-[-0.015em] text-[color:var(--ink)] min-[375px]:text-[16px] md:text-[18px]">
                    “{excerpt.before}
                    <MatchedPhrase
                      index={index}
                      ready={ready}
                      reduced={reduced}
                      emphasized={active === index}
                    >
                      {excerpt.phrase}
                    </MatchedPhrase>
                    {excerpt.after}”
                  </blockquote>
                </motion.figure>
              </li>
            ))}
          </ol>
        </div>

        <div className="relative w-full max-w-[420px] justify-self-center md:justify-self-end">
          <article
            aria-labelledby={briefId}
            className="relative rounded-[4px_16px_16px_16px] border border-[color:var(--hairline)] bg-[color:var(--paper)] p-4 shadow-[0_12px_34px_rgba(15,45,32,0.04)] md:p-5"
          >
            <h3
              id={briefId}
              className="text-[12px] font-medium text-[color:var(--accent-darker)]"
            >
              Recommended messaging
            </h3>
            <div>
              <p className="mt-1 text-[11px] text-[color:var(--ink-3)]">
                Show how teams:
              </p>
              <ul className="m-0 mt-3 list-none space-y-2.5 p-0">
                {EXCERPTS.map((excerpt, index) => (
                  <li
                    key={excerpt.recommendation}
                    id={`${briefId}-recommendation-${index}`}
                    className="relative pl-3.5 text-[14px] font-medium leading-[1.35] text-[color:var(--ink-2)] min-[375px]:text-[16px] md:text-[20px]"
                  >
                    <span
                      ref={(node) => {
                        targetRefs.current[index] = node;
                      }}
                      aria-hidden
                      className={`absolute left-0 top-[calc(0.675em_-_3px)] size-1.5 rounded-full transition-colors motion-reduce:transition-none ${active === index ? 'bg-[color:var(--accent-deep)] shadow-[0_0_0_3px_var(--mint)]' : 'bg-[color:var(--accent-deep)]/40'}`}
                    />
                    <MatchedPhrase
                      index={index}
                      ready={ready}
                      reduced={reduced}
                      emphasized={active === index}
                    >
                      {excerpt.recommendation}
                    </MatchedPhrase>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}
